"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Loader2, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/Input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useProductSearch } from "@/features/shop/customer/customer-shop.queries";
import { getProductImages } from "@/features/shop/types/customer-shop";

/** Mobile-only search entry point: a top-nav icon that opens a full-width overlay
 * with a debounced product search, clickable suggestions, and a "show all results"
 * action that lands on /shop with the query applied. */
export function MobileSearchOverlay() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 350);
  const trimmed = debouncedQuery.trim();
  const { data, isFetching } = useProductSearch(trimmed);
  const results = data?.data ?? [];

  function goToProduct(productId: string) {
    setOpen(false);
    router.push(`/shop/products/${productId}`);
  }

  function showAllResults() {
    const term = query.trim();
    if (!term) return;
    setOpen(false);
    router.push(`/shop?search=${encodeURIComponent(term)}`);
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Search products"
        onClick={() => setOpen(true)}
        className="rounded-full border border-border"
      >
        <Search className="h-4 w-4" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="top"
          showCloseButton={false}
          className="max-h-[85vh] overflow-y-auto"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Search products</SheetTitle>
          </SheetHeader>

          <div className="space-y-4 px-4 pb-4 pt-2">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") showAllResults();
                  }}
                  placeholder="Search products..."
                  className="h-12 rounded-full border-border bg-muted pl-11 text-sm"
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="h-12 shrink-0 rounded-full px-4"
              >
                Cancel
              </Button>
            </div>

            {trimmed.length >= 2 ? (
              isFetching ? (
                <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Searching...
                </div>
              ) : results.length === 0 ? (
                <div className="py-6 text-sm text-muted-foreground">
                  No products found for &ldquo;{trimmed}&rdquo;
                </div>
              ) : (
                <div className="space-y-2">
                  <ul className="space-y-1">
                    {results.map((product) => {
                      const imageUrl = getProductImages(product)[0];
                      const shopName =
                        typeof product.shopId === "object" && product.shopId
                          ? product.shopId.name
                          : undefined;

                      return (
                        <li key={product._id}>
                          <button
                            type="button"
                            onClick={() => goToProduct(product._id)}
                            className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-accent"
                          >
                            <span className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-muted">
                              {imageUrl ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  src={imageUrl}
                                  alt={product.name}
                                  className="h-full w-full object-cover"
                                />
                              ) : null}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-medium text-foreground">
                                {product.name}
                              </span>
                              <span className="block truncate text-xs text-muted-foreground">
                                {shopName ? `${shopName} · ` : ""}KES {product.price}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={showAllResults}
                  >
                    Show all results for &ldquo;{trimmed}&rdquo;
                  </Button>
                </div>
              )
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
